import { useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { selectIsAuthenticated } from "../store/authSlice";
import { WsEventsClient } from "../api/wsClient";

/**
 * Global WS connection that lives while user is authenticated.
 * Accepts `onEvent` callback to dispatch incoming messages.
 * Automatically connects after auth and disconnects on logout.
 */
export function useWsConnection({ onEvent, onStatus } = {}) {
  const isAuth = useSelector(selectIsAuthenticated);
  const clientRef = useRef(null);

  const stableOnEvent = useRef(onEvent);
  stableOnEvent.current = onEvent;

  const stableOnStatus = useRef(onStatus);
  stableOnStatus.current = onStatus;

  const getClient = useCallback(() => clientRef.current, []);

  const sendRequest = useCallback((action, data, timeoutMs) => {
    const client = clientRef.current;
    if (!client) return Promise.reject(new Error("WebSocket not connected"));
    return client.sendRequest(action, data, timeoutMs);
  }, []);

  useEffect(() => {
    if (!isAuth) {
      if (clientRef.current) {
        clientRef.current.close();
        clientRef.current = null;
      }
      return;
    }

    const client = new WsEventsClient({
      onEvent: (msg) => stableOnEvent.current?.(msg),
      onStatus: (s) => stableOnStatus.current?.(s),
    });

    clientRef.current = client;
    client.connect();

    return () => {
      client.close();
      clientRef.current = null;
    };
  }, [isAuth]);

  return { getClient, sendRequest };
}
